import { PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'

import type { PackagesSettingsForm } from '../packages-settings.types'
import { makeNewCarrierNameSchema } from '../packages-settings.types'
import * as S from './carriers-tab.styled'

export const CarriersTab = () => {
  const { t } = useTranslation(['packages-settings'])
  const { control } = useFormContext<PackagesSettingsForm>()
  const [newCarrierName, setNewCarrierName] = useState('')
  const [error, setError] = useState('')

  const { fields, append, remove } = useFieldArray<PackagesSettingsForm, 'carriers'>({
    control,
    name: 'carriers',
  })

  const carriers = useWatch<PackagesSettingsForm, 'carriers'>({ name: 'carriers' })

  const handleAdd = () => {
    const trimmed = newCarrierName.trim()
    if (!trimmed) return

    const result = makeNewCarrierNameSchema(t, carriers ?? []).safeParse(trimmed)
    if (!result.success) {
      setError(result.error.issues[0].message)
      return
    }

    append({ name: trimmed })
    setNewCarrierName('')
    setError('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAdd()
  }

  return (
    <S.Container>
      {fields.length > 0 && (
        <S.Table>
          {fields.map((field, index) => (
            <S.Row key={field.id}>
              <Typography size="text-medium" weight="medium" color="black600">
                {field.name}
              </Typography>
              <Button onlyBox use="secondary" size="small" onClick={() => remove(index)}>
                <Icon color="red500" icon={TrashIcon} size="1.6rem" />
              </Button>
            </S.Row>
          ))}
        </S.Table>
      )}

      <S.AddSection>
        <Typography size="text-medium" weight="medium">
          {t('packages-settings:carriersTab.addPrompt')}
        </Typography>
        <S.AddRow>
          <TextInput
            placeholder={t('packages-settings:carriersTab.addPlaceholder')}
            value={newCarrierName}
            error={error}
            onChange={(e) => {
              setNewCarrierName(e.target.value)
              setError('')
            }}
            onKeyDown={handleKeyDown}
          />
          <Button
            use="secondary"
            size="medium"
            disabled={!newCarrierName.trim()}
            onClick={handleAdd}
          >
            {t('packages-settings:carriersTab.addCarrier')}
            <Icon icon={PlusIcon} />
          </Button>
        </S.AddRow>
      </S.AddSection>
    </S.Container>
  )
}
